import React, { useState, useCallback, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import httpRequest from "../utils/httpRequest";

const ProductSearch = ({
  value,
  onChange,
  onInputChange,
  label = "Product Name",
  placeholder = "Type to search products...",
  fullWidth = true,
  required = false,
  disabled = false,
  inputRef,
  error = false,
  helperText = "",
  minSearchLength = 2,
  debounceMs = 300,
  maxResults = 20,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const searchProducts = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < minSearchLength) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await httpRequest(
        `products/search?search=${encodeURIComponent(searchTerm)}&limit=${maxResults}`,
        "GET"
      );
      setSearchResults(response.products || []);
    } catch (error) {
      console.error("Error searching products:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [minSearchLength, maxResults]);

  // Debounced search handler
  const handleSearch = useCallback((searchTerm) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      searchProducts(searchTerm);
    }, debounceMs);

    setSearchTimeout(timeout);
  }, [searchTimeout, searchProducts, debounceMs]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleInputChange = (event, newInputValue) => {
    if (onInputChange) {
      onInputChange(event, newInputValue);
    }
    handleSearch(newInputValue);
  };

  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(event, newValue);
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={searchResults}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        return option.name || "";
      }}
      filterOptions={(x) => x}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      loading={isSearching}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth={fullWidth}
          required={required}
          inputRef={inputRef}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isSearching ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body1">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              HSN: {option.hsn}
            </Typography>
          </Box>
        </Box>
      )}
      noOptionsText={
        value && value.length >= minSearchLength
          ? "No products found"
          : `Type at least ${minSearchLength} characters to search`
      }
    />
  );
};

export default ProductSearch; 